import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Text, 
  HStack, 
  Button, 
  useBreakpointValue, 
  Flex,
  TableContainer,
  Divider,
  Badge
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { parseISO, differenceInMinutes } from 'date-fns';
import { Event, EventStatus } from '../../types/types';
import { EventStatusBadge } from './EventStatusBadge';
import { EventTeamInfo } from './EventTeamInfo';
import { formatDate, getStreamIdBackgroundColor } from '../../utils/eventUtils';
import { formatTimeInTz, tzShortLabel } from '../../utils/timezone';
import { CompactUrlDisplay } from '../common/CompactUrlDisplay';

interface EventsTableProps {
  date: string;
  events: Event[];
  timeZone: string;
  onCancelEvent: (eventId: number) => Promise<void>;
}

export const EventsTable: React.FC<EventsTableProps> = ({ date, events, timeZone, onCancelEvent }) => {
  const navigate = useNavigate();
  const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const tableSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // State to force re-renders for dimming updates
  const [updateCounter, setUpdateCounter] = useState(0);

  // Function to check if event started more than 5 minutes ago
  const isOlderEvent = (startTime: string): boolean => {
    const now = new Date();
    const eventStartTime = parseISO(startTime);
    return differenceInMinutes(now, eventStartTime) > 5;
  };

  // Set up timer to update dimmed status every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setUpdateCounter(prev => prev + 1);
    }, 30000); // 30 seconds
    
    return () => clearInterval(timer); // Cleanup on unmount
  }, []);

  // Group events by dimmed status
  const groupEventsByDimStatus = () => {
    const recentEvents: Event[] = [];
    const olderEvents: Event[] = [];
    
    events.forEach(event => {
      if (isOlderEvent(event.startTime)) {
        olderEvents.push(event);
      } else {
        recentEvents.push(event);
      }
    });
    
    return { recentEvents, olderEvents };
  };

  // Re-calculate groups on each render (including timer-triggered renders)
  const { recentEvents, olderEvents } = groupEventsByDimStatus();

  // Render a single event row
  const renderEventRow = (event: Event) => {
    const isOlder = isOlderEvent(event.startTime);
    const streamIdBackgroundColor = getStreamIdBackgroundColor(event.streamId);
    
    return (
      <Tr 
        key={event.id} 
        _hover={{ bg: 'gray.50' }}
        opacity={isOlder ? 0.7 : 1}
      >
        {!isMobile && (
          <Td py={{ base: 1, md: 2 }}>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">{event.id}</Text>
          </Td>
        )}
        {!isMobile && (
          <Td py={{ base: 1, md: 2 }}>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">{event.providerEventId}</Text>
          </Td>
        )}
        <Td py={{ base: 1, md: 2 }}>
          <Text fontSize={{ base: 'xs', md: 'sm' }}>{formatTimeInTz(event.startTime, timeZone)}</Text>
        </Td>
        <Td py={{ base: 1, md: 2 }}>
          <EventTeamInfo event={event} />
        </Td>
        <Td py={{ base: 1, md: 2 }}>
          <EventStatusBadge status={event.status} />
        </Td>
        <Td py={{ base: 1, md: 2 }}>
          {event.streamId !== undefined ? (
            <Badge backgroundColor={streamIdBackgroundColor} px={2} py={1} borderRadius="md">
              {event.streamId}
            </Badge>
          ) : (
            <Text fontSize="xs" color="gray.500">-</Text>
          )}
        </Td>
        {!isMobile && (
          <Td py={{ base: 1, md: 2 }}>
            <CompactUrlDisplay url={event.videoUrl} maxWidth="300px" />
          </Td>
        )}
        <Td py={{ base: 1, md: 2 }}>
          <Flex gap={1} direction={{ base: 'column', sm: 'row' }} align="flex-start">
            <Button
              as="a"
              href={`/events/${event.id}`}
              target="_blank"
              rel="noopener noreferrer"
              size={buttonSize}
              colorScheme="blue"
              width={{ base: '100%', sm: 'auto' }}
            >
              Details
            </Button>
            {event.status === EventStatus.ACTIVE && (
              <Button
                size={buttonSize}
                colorScheme="red"
                onClick={() => onCancelEvent(event.id)}
                width={{ base: '100%', sm: 'auto' }}
              >
                Cancel
              </Button>
            )}
            {isMobile && event.videoUrl && (
              <Button
                size={buttonSize}
                colorScheme="teal"
                onClick={() => navigator.clipboard.writeText(event.videoUrl || '')}
                width={{ base: '100%', sm: 'auto' }}
              >
                Copy URL
              </Button>
            )}
          </Flex>
        </Td>
      </Tr>
    );
  };

  return (
    <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden" mb={4}>
      <Box bg="gray.50" p={{ base: 2, md: 3 }}>
        <Text fontWeight="medium">
          {formatDate(date)}
        </Text>
      </Box>
      <TableContainer overflowX="auto">
        <Table variant="simple" size={tableSize}>
          <Thead>
            <Tr>
              {!isMobile && <Th py={{ base: 1, md: 2 }}>ID</Th>}
              {!isMobile && <Th py={{ base: 1, md: 2 }}>Provider ID</Th>}
              <Th py={{ base: 1, md: 2 }}>Start Time ({tzShortLabel(timeZone)})</Th>
              <Th py={{ base: 1, md: 2 }}>Teams</Th>
              <Th py={{ base: 1, md: 2 }}>Status</Th>
              <Th py={{ base: 1, md: 2 }}>Stream ID</Th>
              {!isMobile && <Th py={{ base: 1, md: 2 }}>Video URL</Th>}
              <Th py={{ base: 1, md: 2 }} width={{ base: '100px', md: '150px' }}>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* Recent events */}
            {recentEvents.map(renderEventRow)}
            
            {/* Divider between recent and older events */}
            {recentEvents.length > 0 && olderEvents.length > 0 && (
              <Tr>
                <Td colSpan={isMobile ? 5 : 8} py={0} px={0}>
                  <Box borderTop="3px solid" borderColor="gray.300" my={1} />
                </Td>
              </Tr>
            )}
            
            {/* Older events */}
            {olderEvents.map(renderEventRow)}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
